import React, { forwardRef, useImperativeHandle, useState } from 'react'
import {
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  TextInput,
  TouchableWithoutFeedback
} from 'react-native'

import { DS } from '../../design-system'
import { LoadingContent } from './LoadingContent'
import { styles } from './styles'

export interface ReportResponseRef {
  openModal: () => void
  closeModal: () => void
}

type ReportResponseProps = {
  onClose: () => void
  reportResponseId: string
  isReportSubmitted: boolean
  setIsReportSubmitted: (isReportSubmitted: boolean) => void
  onReport: (feedback: string, reportResponseId: string) => void
}

export const ReportResponseSheet = forwardRef((props: ReportResponseProps, ref) => {
  const { onClose, onReport, isReportSubmitted, setIsReportSubmitted, reportResponseId } = props

  const [isReportModalVisible, setIsReportModalVisible] = useState(false)
  const [reportReason, setReportReason] = useState('')
  const [isReporting, setIsReporting] = useState(false)

  const resetReportSheet = () => {
    setReportReason('')
    setIsReportSubmitted(false)
    setIsReportModalVisible(false)
    setIsReporting(false)
  }

  // Exposing methods to parent using ref
  useImperativeHandle(ref, () => ({
    openModal: () => {
      setReportReason('')
      setIsReportModalVisible(true)
    },
    closeModal: () => {
      resetReportSheet()
    }
  }))

  React.useEffect(() => {
    return () => {
      resetReportSheet()
    }
  }, [])

  return (
    <Modal transparent visible={isReportModalVisible}>
      <TouchableWithoutFeedback
        onPress={(e) => {
          e.stopPropagation()
          Keyboard.dismiss()
          onClose()
        }}
        style={{ flex: 1 }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 25}
          style={styles.keyboardAvoidingContainer}
        >
          {/**
           * Inner TouchableWithoutFeedback will prevent closing of modal on tapping the modal content
           */}
          <TouchableWithoutFeedback>
            <DS.Frame
              bg="Brand/Cream"
              style={{ width: '100%', padding: 16, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
            >
              {isReporting && !isReportSubmitted ? (
                <LoadingContent />
              ) : (
                <React.Fragment key={'REPORT'}>
                  {!isReportSubmitted ? (
                    <ScrollView keyboardShouldPersistTaps contentContainerStyle={{ flexGrow: 1 }}>
                      <React.Fragment key={'user-report-input'}>
                        <DS.Text variant="Heading 2/Medium" textAlign="center">
                          Report an issue
                        </DS.Text>
                        <DS.Frame row alignItems="center" style={{ marginTop: 16 }}>
                          <TextInput
                            autoFocus={true}
                            placeholder="Reason for reporting"
                            multiline={true}
                            maxLength={750}
                            onChangeText={(text) => {
                              setReportReason(text)
                            }}
                            value={reportReason}
                            placeholderTextColor={DS.palette['Brand/Dark Grey']}
                            style={[styles.userResponseInput, { width: '100%' }]}
                          />
                        </DS.Frame>
                        <DS.Frame
                          row
                          justifyContent="space-between"
                          alignItems="center"
                          style={{ marginTop: 5, padding: 10 }}
                        >
                          <DS.Button
                            variant={'Large/Primary'}
                            type={'On Dark'}
                            backgroundColor={'Brand/Cream'}
                            borderColor={'Brand/Light Grey'}
                            style={styles.buttonView}
                            onPress={(e) => {
                              Keyboard.dismiss()
                              setTimeout(() => {
                                setReportReason('')
                                onClose()
                              }, 100)
                            }}
                          >
                            <DS.Text variant="Body Medium/Medium" color="Global/Black" textAlign="center">
                              Cancel
                            </DS.Text>
                          </DS.Button>
                          <DS.Button
                            disabled={reportReason.trim().length < 12 || reportReason.trim().length > 750}
                            variant={'Large/Primary'}
                            type={'On Dark'}
                            backgroundColor={'Brand/Dark Grey'}
                            borderColor={'Brand/Light Grey'}
                            style={[styles.buttonView, { backgroundColor: DS.palette['Brand/Dark Grey'] }]}
                            onPress={() => {
                              const trimmedResponse = reportReason.trim()
                              if (trimmedResponse.length > 0) {
                                setIsReporting(true)
                                Keyboard.dismiss()
                                setTimeout(() => {
                                  setReportReason('')
                                  onReport(reportReason, reportResponseId)
                                }, 100)
                              }
                            }}
                          >
                            <DS.Text variant="Body Medium/Medium" color="Global/White" textAlign="center">
                              Report
                            </DS.Text>
                          </DS.Button>
                        </DS.Frame>
                      </React.Fragment>
                    </ScrollView>
                  ) : (
                    <DS.Frame style={styles.feedbackContainer}>
                      <DS.Frame style={{ alignItems: 'center' }}>
                        <DS.Icon name="Check--enclosed" size={48} color={'Global/Black'} />
                        <DS.Text variant="Heading 3/Medium" style={{ marginVertical: 8 }}>
                          Thanks for your feedback!
                        </DS.Text>
                        <DS.Text variant="Body Large/Regular" textAlign="center">
                          Our support team will reach out if we need more details.
                        </DS.Text>
                      </DS.Frame>
                      <DS.Button
                        variant={'Large/Primary'}
                        type={'On Dark'}
                        backgroundColor={'Brand/Cream'}
                        borderColor={'Brand/Light Grey'}
                        style={{ marginTop: 15 }}
                        onPress={() => {
                          Keyboard.dismiss()
                          setTimeout(() => {
                            onClose()
                          }, 100)
                        }}
                      >
                        <DS.Text variant="Body Medium/Medium" color="Global/Black" textAlign="center">
                          Close
                        </DS.Text>
                      </DS.Button>
                    </DS.Frame>
                  )}
                </React.Fragment>
              )}
            </DS.Frame>
          </TouchableWithoutFeedback>
        </KeyboardAvoidingView>
      </TouchableWithoutFeedback>
    </Modal>
  )
})
